import { useCallback, useState } from 'react';
import { reportError } from 'utils/reporting';
import { showNotification } from 'common/function';
import { isApiError } from 'common/types';
import { useCurrentUser } from './useCurrentUser';
import { FeatureFlag, TenancyService } from 'types';
import { useFeatureFlag } from 'hooks/featureFlags/useFeatureFlag';
import { LegacyTenancyService } from 'service/TenancyService';
import { getAuthCookie, setAuthCookie } from 'utils/auth';
import { refreshAuthToken } from 'service/cognitoService';

const refreshTokenAfterSwitch = async () => {
  const auth = getAuthCookie();
  if (!auth?.refresh_token) {
    return;
  }
  try {
    const newTokens = await refreshAuthToken(auth.refresh_token);
    setAuthCookie({ ...auth, ...newTokens });
  } catch (err) {
    reportError(err);
  }
};

export function useTenantActions() {
  const { user } = useCurrentUser();
  const [isLoading, setIsLoading] = useState(false);

  const isTenantManagementEnabled = useFeatureFlag(FeatureFlag.TENANCY_MANAGEMENT);

  const switchTenant = useCallback(async (tenantId: string) => {
    if (!user) {
      return;
    }

    setIsLoading(true);

    try {
      const result = isTenantManagementEnabled ? await TenancyService.switchTenancy({
        requestBody: {
          tenantId,
        },
      }) : await LegacyTenancyService.switchTenancy({ tenantId, user });
      await refreshTokenAfterSwitch();
      return result;
    } catch (err: any) {
      reportError(err);
      showNotification(isApiError(err) ? err.body.error : err.message, 'Load Tenants Error', 'error');
    }
    setIsLoading(false);
  }, [user, isTenantManagementEnabled]);

  const returnToHomeTenant = useCallback(async () => {
    if (!user) {
      return;
    }

    setIsLoading(true);

    try {
      const result = isTenantManagementEnabled ? await TenancyService.resetTenancy() : await LegacyTenancyService.resetTenancy(user);
      await refreshTokenAfterSwitch();
      return result;
    } catch (err: any) {
      reportError(err);
      showNotification(isApiError(err) ? err.body.error : err.message, 'Load Tenants Error', 'error');
    }
    setIsLoading(false);
  }, [user, isTenantManagementEnabled]);

  return {
    isLoading,
    switchTenant,
    returnToHomeTenant,
  };
}
